import { useEffect } from 'react';

/**
 * SEOHead - Injects dynamic SEO metadata and JSON-LD structured data into <head>.
 * React SPA equivalent of server-side metadata rendering.
 * Updates document.title and meta tags on mount and when props change.
 */
export default function SEOHead({ metadata, schemas, hreflang }) {
  useEffect(() => {
    if (!metadata) return;

    // Title
    if (metadata.title) {
      document.title = metadata.title;
    }

    // Helper to set/create meta tags
    const setMeta = (attr, attrVal, content) => {
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Helper to set/create link tags
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Standard meta
    if (metadata.description) setMeta('name', 'description', metadata.description);
    if (metadata.robots) setMeta('name', 'robots', metadata.robots);

    // Open Graph
    const ogTitle = metadata.og_title || metadata.title;
    const ogDesc = metadata.og_description || metadata.description;
    if (ogTitle) setMeta('property', 'og:title', ogTitle);
    if (ogDesc) setMeta('property', 'og:description', ogDesc);
    setMeta('property', 'og:type', 'website');
    if (metadata.canonical) {
      const prodDomain = 'https://hustle-hubai.com';
      const absUrl = metadata.canonical.startsWith('http') ? metadata.canonical : `${prodDomain}${metadata.canonical}`;
      setMeta('property', 'og:url', absUrl);
    }
    setMeta('property', 'og:image', metadata.og_image || 'https://hustle-hubai.com/og-image.png');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');

    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    if (ogTitle) setMeta('name', 'twitter:title', ogTitle);
    if (ogDesc) setMeta('name', 'twitter:description', ogDesc);
    setMeta('name', 'twitter:image', metadata.og_image || 'https://hustle-hubai.com/og-image.png');

    // Canonical — update the existing one set by index.html early script
    if (metadata.canonical) {
      const prodDomain = 'https://hustle-hubai.com';
      const canonicalHref = metadata.canonical.startsWith('http') ? metadata.canonical : `${prodDomain}${metadata.canonical}`;
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', canonicalHref);
    }

    return () => {
      // Cleanup on unmount - reset to defaults
      document.title = 'Hustle-HubAI';
    };
  }, [metadata]);

  // Inject hreflang alternate link tags
  useEffect(() => {
    if (!hreflang?.length) return;

    // Remove old hreflang links
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());

    // Add new hreflang links
    hreflang.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', href);
      link.setAttribute('data-hreflang', 'true');
      document.head.appendChild(link);
    });

    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    };
  }, [hreflang]);

  // Inject JSON-LD schemas
  useEffect(() => {
    if (!schemas?.length) return;

    // Remove old schema scripts
    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());

    // Homepage de-dup: the static <head> in index.html already carries
    // canonical Organization + WebSite schemas. The /api/seo/schema/home
    // endpoint returns its own copies — skip them here on "/" only so
    // AEO crawlers don't see duplicates. Inner pages are unaffected.
    const isHomepage = typeof window !== 'undefined'
      && (window.location.pathname === '/' || window.location.pathname === '');
    const filtered = isHomepage
      ? schemas.filter(s => {
          const t = s && s['@type'];
          return t !== 'Organization' && t !== 'WebSite';
        })
      : schemas;

    // Add new ones
    filtered.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', `schema-${i}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    };
  }, [schemas]);

  return null;
}
